import _ from 'lodash';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';

import { openModalAction } from '../../../actions/modal';
import { ActionButton, ActionDropdownSingleChoiceMenu, ActionWidget } from '../../../components/ActionWidget';
import { PageHeader } from '../../../components/PageHeader';
import { DATE_TIME_FORMAT_12_UPPERCASE_TIMEZONE } from '../../../constants';
import Strings from '../../../Strings';
import './messages.scss';
import { actions } from './redux/actions';
import { TextInput } from '../../../components/PageHeader/TextInput';
import { Button } from '../../../components/PageHeader/Button';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import { toolTipForTable } from '../../../utils';
import { GET_INBOX_ARCHIVED_RESULT, GET_INBOX_MESSAGES_RESULT } from './redux/constants';
import {
  getSearchParamFromUrl,
  hydrateTableRequestFromUrl,
  saveSearchParamToUrl,
  useGlobalPageSize,
} from '../../../utils/tableUtils';

export const Tabs = {
  INBOX: 'inbox',
  ARCHIVED: 'archived',
}

function MessageList({ onNavigate, getInbox, getArchived, toggleArchieved, toggleRead, tab, orgIds, orgs }) {
  const globalPageSize = useGlobalPageSize();
  const pageRequest = useRef({
    offset: 0,
    search: getSearchParamFromUrl(),
    limit: globalPageSize,
    orgId: (orgIds && orgIds.length > 0) ? orgIds : undefined,
  });
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({});
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [searchText, setSearchText] = useState(getSearchParamFromUrl() || '');
  const refreshEnable = useRef(true);
  const tableRef = React.useRef(null);
  const hasLoadedOnce = useRef(false);

  const onMessagesReceived = (resp, clearDataBeforeFetch) => {
    const newPagination = resp.response?.pagination || {};
    if (resp.type === GET_INBOX_MESSAGES_RESULT || resp.type === GET_INBOX_ARCHIVED_RESULT) {
      setMessages(resp.response.data || []);
      setPagination(newPagination);
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  };

  const loadMessages = clearDataBeforeFetch => {
    if (Number.isNaN(pageRequest.current.limit) || pageRequest.current.limit <= 0) {
      return;
    }
    if (clearDataBeforeFetch) {
      setMessages([]);
      setPagination({});
    }
    setIsLoading(true);
    if (tab === Tabs.INBOX) {
      getInbox(pageRequest.current).then(resp => {
        onMessagesReceived(resp, clearDataBeforeFetch);
      });
    } else if (tab === Tabs.ARCHIVED) {
      getArchived(pageRequest.current).then(resp => {
        onMessagesReceived(resp, clearDataBeforeFetch);
      });
    }
    tableRef?.current?.resetSelectedRows();
  };

  useEffect(() => {
    pageRequest.current.orgId = (orgIds && orgIds.length > 0) ? orgIds : undefined;

    if (!hasLoadedOnce.current) {
      hydrateTableRequestFromUrl(pageRequest.current, globalPageSize);
      hasLoadedOnce.current = true;
    } else {
      pageRequest.current.offset = 0;
    }

    loadMessages(true);
  }, [globalPageSize, orgIds]);

  const onPageSizeChange = limit => {
    pageRequest.current.limit = limit;
    pageRequest.current.offset = 0;
    loadMessages(true);
  };

  const onOffsetChange = (offset, limit) => {
    if (offset === pageRequest.current.offset && limit === pageRequest.current.limit) {
      return;
    }
    pageRequest.current.offset = offset;
    if (limit) {
      pageRequest.current.limit = limit;
    }
    loadMessages();
  };

  const onSortingChange = sorting => {
    const sortColumn = sorting?.length > 0 ? sorting[0].id : undefined;
    const sortType = sorting?.length > 0 ? (sorting[0].desc ? 'descending' : 'ascending') : undefined;
    if (sortColumn === pageRequest.current.sortColumn && sortType === pageRequest.current.sortType) {
      return;
    }
    pageRequest.current.sortColumn = sortColumn;
    pageRequest.current.sortType = sortType;
    pageRequest.current.offset = 0;
    loadMessages(true);
  };

  const onSearchQueryChangeDebounced = _.debounce((query) => {
    pageRequest.current.offset = 0;
    pageRequest.current.search = query;
    saveSearchParamToUrl(query);
    loadMessages(true);
  }, 1000);

  const onSearchQueryChange = e => {
    const query = e.target.value;
    setSearchText(query);
    return onSearchQueryChangeDebounced(query);
  };

  const getHeaderComponentsLeft = () => {
    return (
      <div className="unread-count"> {pagination?.totalUnreadCount ?? 0} Unread Messages</div>
    );
  };

  const onRefresh = () => {
    refreshEnable.current = false;
    setTimeout(() => {
      refreshEnable.current = true;
    }, 30000);
    loadMessages(true);
  };

  const getSecondHeaderComponents = () => {
    return (
      <>
        <TextInput className="search" placeholder={Strings.search} value={searchText} onChange={onSearchQueryChange} />
        <Button class="refresh" disabled={!refreshEnable.current} onClick={onRefresh} />
      </>
    );
  };

  const onRowSelected = (message) => {
    onNavigate(`/messages/inbox/${message.userId}`);
  };

  const onArchiveToggle = () => {
    selectedMessages.forEach(message => {
      const data = { id: parseInt(message.userId, 10), value: tab === Tabs.INBOX };

      toggleArchieved(data).then(() => {
        loadMessages(true);
      });
    });
  };

  const onReadToggle = value => {
    selectedMessages.forEach(message => {
      const data = { id: message.userId, value };

      toggleRead(data).then(() => {
        loadMessages(true);
      });
    });
  };

  const columns = React.useMemo(() => {
    const ret = [
      columnHelper.accessor('direction', {
        cell: info => info.row.original.isIncoming ? Strings.messages.from : Strings.messages.to,
        header: Strings.messages.toFrom,
        size: 100,
        enableSorting: false,
      }),

      columnHelper.accessor('mobileNumber', {
        cell: info => <>{info.getValue() ? info.getValue() : '-'}  {!info.row.original.read ? <div className="unread-message"></div> : ''}</>,
        header: Strings.messages.number,
        enableSorting: false,
      }),

      columnHelper.accessor('date', {
        cell: info => (info.getValue() ? moment(info.getValue()).format(DATE_TIME_FORMAT_12_UPPERCASE_TIMEZONE) : '-'),
        header: Strings.messages.date,
        accessorFn: row => row.lastMessageTime,
        size: 220,
      }),

      columnHelper.accessor('name', {
        cell: info => info.getValue() ? info.getValue() : '-',
        header: Strings.messages.name,
        accessorFn: row => [row.firstName, row.lastName].filter(Boolean).join(' ') || '-',
      }),
    ];

    if (orgs?.length > 1) {
      ret.push(columnHelper.accessor('organization', {
        header: Strings.organization,
      }));
    }

    ret.push(
      columnHelper.accessor('lastMessage', {
        cell: info => (info.getValue() ? toolTipForTable(info.getValue()) : '-'),
        header: Strings.messages.conversation,
        minSize: 400,
        size: 600,
        enableSorting: false,
      })
    );
    return ret;
  }, []);

  return (
    <div className="billing-container">
      <PageHeader left={() => getHeaderComponentsLeft()} right={() => getSecondHeaderComponents()} />
      <Table3
        name={`message-table-${orgs?.length}`}
        isLoading={isLoading && messages.length === 0}
        data={messages || []}
        columns={columns}
        pagination={pagination}
        onRowClick={onRowSelected}
        onOffsetChange={onOffsetChange}
        onPageSizeChange={onPageSizeChange}
        onSortingChange={onSortingChange}
        onRowSelectionChange={setSelectedMessages}
        enableColumnFiltering
        enableRowSelection
        pinFirstColumn
        ref={tableRef}
      />
      <ActionWidget show={selectedMessages?.length > 0}>
        <ActionButton
          img="action-archive"
          tooltiptext={tab === Tabs.INBOX ? "Archive" : "Unarchive"}
          text={tab === Tabs.INBOX ? "Archive" : "Unarchive"}
          action={onArchiveToggle}
        />
        <ActionDropdownSingleChoiceMenu
          img="action-unread"
          tooltiptext={Strings.messages.markAs}
          options={[
            {
              value: true,
              text: Strings.messages.read,
            },
            {
              value: false,
              text: Strings.messages.unread,
            },
          ]}
          action={onReadToggle}
        />
      </ActionWidget>
    </div>
  );
}

MessageList.propTypes = {
  messages: PropTypes.array,
  isLoading: PropTypes.bool,
  getInbox: PropTypes.func,
  getArchived: PropTypes.func,
  onNavigate: PropTypes.func,
  pagination: PropTypes.shape({
    offset: PropTypes.number,
    total: PropTypes.number,
    totalUnreadCount: PropTypes.any,
  }),
  toggleArchieved: PropTypes.func,
  toggleRead: PropTypes.func,
  tab: PropTypes.string,
  orgIds: PropTypes.arrayOf(PropTypes.string),
  orgs: PropTypes.array,
};

const mapStateToProps = state => {
  return {
    orgIds: state.entities?.orgFilter?.orgIds,
    orgs: state.entities?.orgFilter?.orgs,
  };
};

const mapDispatchToProps = {
  onNavigate: push,
  getInbox: actions.getInboxMessages,
  getArchived: actions.getArchivedMessages,
  toggleArchieved: actions.archiveToggle,
  toggleRead: actions.readToggle,
  openConfirmModal: openModalAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(MessageList);
